import { Browser } from '@capacitor/browser';
import { CoreoAPI } from '../../services/api.service';

import { decodeAuthToken, localLogin, localSignup, recover2FACode, verify2FAToken, ssoLogin } from '../../services/auth.service';
import SQLDatabase from '../../services/db/sql.service';
import { CoreoUser } from '../../types';
import { TypeKeys } from '../actions';
import { getAuthUser } from '../selectors';

let signupPollHandle: number;

export interface AuthStartAction {
  type: TypeKeys.AUTH_START;
}

export interface AuthSuccessAction {
  type: TypeKeys.AUTH_SUCCESS;
  token: string;
  user: CoreoUser;
}

export interface AuthFailAction {
  type: TypeKeys.AUTH_FAIL;
  error: string;
}

export interface AuthRestartAction {
  type: TypeKeys.AUTH_RESTART;
}

export interface AuthVerifyTokenStartAction {
  type: TypeKeys.AUTH_VERIFY_START;
}

export interface AuthVerifyTokenSuccessAction {
  type: TypeKeys.AUTH_VERIFY_SUCCESS;
  token: string;
  user: CoreoUser;
}

export interface AuthVerifyTokenFailAction {
  type: TypeKeys.AUTH_VERIFY_FAIL;
  error: string;
}

export interface AuthSetSignupProjectIdAction {
  type: TypeKeys.AUTH_SET_SIGNUP_PROJECT_ID,
  projectId
}

export interface AuthLogoutAction {
  type: TypeKeys.AUTH_LOGOUT;
}

export type AuthActions =
  | AuthStartAction
  | AuthSuccessAction
  | AuthFailAction
  | AuthRestartAction
  | AuthVerifyTokenStartAction
  | AuthVerifyTokenSuccessAction
  | AuthVerifyTokenFailAction
  | AuthSetSignupProjectIdAction
  | AuthLogoutAction;

export const authStart = (): AuthStartAction => ({
  type: TypeKeys.AUTH_START
});

export const authRestart = (): AuthRestartAction => ({
  type: TypeKeys.AUTH_RESTART
});

export const authSuccess = (token: string, user: CoreoUser): AuthSuccessAction => ({
  type: TypeKeys.AUTH_SUCCESS,
  token,
  user
});

export const authFail = (error: string): AuthFailAction => ({
  type: TypeKeys.AUTH_FAIL,
  error
});

export const authVerifyTokenStart = (): AuthVerifyTokenStartAction => ({
  type: TypeKeys.AUTH_VERIFY_START
});

export const authVerifyTokenSuccess = (token: string, user: CoreoUser): AuthVerifyTokenSuccessAction => ({
  type: TypeKeys.AUTH_VERIFY_SUCCESS,
  token,
  user
});

export const authVerifyTokenFail = (error: string): AuthVerifyTokenFailAction => ({
  type: TypeKeys.AUTH_VERIFY_FAIL,
  error
});

export const auth = (email: string, password: string) => async dispatch => {
  dispatch(authStart());

  try {
    const { token, pre2FAToken } = await localLogin(email, password);
    if (!token && !pre2FAToken) throw new Error('No token available');

    if (pre2FAToken) return pre2FAToken;

    const user: CoreoUser = decodeAuthToken(token);
    CoreoAPI.instance.setAuthToken(token);
    dispatch(authSuccess(token, user));
  } catch (e) {
    dispatch(authFail(e.message));
    throw e;
  }
}

export const authSSO = (domain: string, platform: string) => async dispatch => {
  dispatch(authStart());

  try {
    const { redirectUrl } = await ssoLogin(domain, platform);

    if (platform === 'web') {
      const url = new URL(redirectUrl);
      url.searchParams.set('returnUrl', window.location.href);
      window.location.href = url.toString();
      return;
    }
    Browser.open({
      url: redirectUrl
    });
  } catch (e) {
    dispatch(authFail(e.message));
    throw e;
  }
};

export const verifySSOLogin = (token: string) => async dispatch => {
  dispatch(authVerifyTokenStart());

  try {
    const user: CoreoUser = decodeAuthToken(token);

    CoreoAPI.instance.setAuthToken(token);
    dispatch(authVerifyTokenSuccess(token, user));
  } catch (e) {
    dispatch(authFail(e.message));
    throw e;
  }
};

export const verify2FA = (pre2FAToken: string, code: string) => async dispatch => {
  dispatch(authVerifyTokenStart());

  try {
    const token = await verify2FAToken(pre2FAToken, code);

    const user: CoreoUser = decodeAuthToken(token);
    CoreoAPI.instance.setAuthToken(token);
    dispatch(authVerifyTokenSuccess(token, user));
  } catch (e) {
    dispatch(authVerifyTokenFail(e.message));
    throw e;
  }
}

export const recover2FA = (pre2FAToken: string, code: string) => async dispatch => {
  dispatch(authVerifyTokenStart());

  try {
    const token = await recover2FACode(pre2FAToken, code);

    const user: CoreoUser = decodeAuthToken(token);
    CoreoAPI.instance.setAuthToken(token);
    dispatch(authVerifyTokenSuccess(token, user));
  } catch (e) {
    dispatch(authVerifyTokenFail(e.message));
    throw e;
  }
}

export const signup = (email: string, password: string, displayName: string, marketing: boolean, projectId: number) => async dispatch => {
  dispatch(authStart());

  try {
    await localSignup(email, password, displayName, marketing, projectId);

    signupPollHandle = window.setInterval(async () => {
      try {
        await auth(email, password)(dispatch);
        window.clearInterval(signupPollHandle);
        signupPollHandle = null;
      } catch (e) {
        console.log('Not ready yet:', e);
      }
    }, 3000);
  } catch (e) {
    dispatch(authFail(e.message));
    throw e;
  }
}

export const signupPollCancel = () => {
  if (signupPollHandle) {
    window.clearInterval(signupPollHandle);
  }
}

export const authLogout = () => async dispatch => {
  dispatch({
    type: TypeKeys.AUTH_LOGOUT
  });

  CoreoAPI.instance.setAuthToken(null);
};

export const authUpdateUser = (username: string, displayName: string, blob?: Blob) => async (dispatch, getState) => {
  dispatch({
    type: TypeKeys.AUTH_UPDATE_USER
  })

  const formData = new FormData();

  if (blob) {
    const extension = blob.type.split('/')[1];
    formData.append('imageUrl', new File([blob], `avatar.${extension}`, { type: blob.type }));
  }

  const mutation = `mutation{
    user: updateProfile( input:{
      username: "${username}",
      displayName: "${displayName}"
    }) {
      token,
      user{
        id,
        imageUrl,
        username,
        displayName
      }
    }
  }`

  formData.append('query', mutation);
  const response = await CoreoAPI.instance.post(`/graphql`, formData);
  if (!response.ok) {
    const error = await response.statusText;
    dispatch({
      type: TypeKeys.AUTH_UPDATE_USER_FAILURE,
      error
    })
    throw error;
  }
  const res = await response.json();
  const oldUser = getAuthUser(getState());
  const imageUrl = res.data.user.user.imageUrl;
  await SQLDatabase.instance.query(`UPDATE users SET username = ?, displayName = ?, imageUrl = ? WHERE id = ?`, [username, displayName, oldUser.userId, imageUrl]);

  dispatch({
    type: TypeKeys.AUTH_UPDATE_USER_SUCCESS,
    user: {
      ...oldUser,
      displayName,
      username,
      imageUrl
    }
  });
}

export const authSetSignupProjectId = (projectId: number): AuthSetSignupProjectIdAction => ({
  type: TypeKeys.AUTH_SET_SIGNUP_PROJECT_ID,
  projectId
});

export const authLoginWithDebugToken = (token: string) => async dispatch => {
  dispatch(authStart());

  try {
    const user: CoreoUser = decodeAuthToken(token);
    CoreoAPI.instance.setAuthToken(token);
    dispatch(authSuccess(token, user));
  } catch (e) {
    dispatch(authFail(e.message));
    throw e;
  }
}